import React from 'react'
import { connect } from 'react-redux'
import { Helmet } from 'react-helmet'
import styled from 'styled-components'
import { Formik, Form } from 'formik'
import { FaArrowLeft } from 'react-icons/fa'
import { ClipLoader } from 'react-spinners'

// Configs
import { borrowingBaseCertificateConfig } from '../_configs'

// Components
import { FormTracker } from '../_components/FormTracker'
import { S_FormTracker } from '../_components/FormTracker/FormTracker.styles'
import Slider from '../_components/Slider'
import { BBCSummary } from './BBCSummary'
import { BBCFormSection } from './BBCFormSection'
import { BBCARUploadStep } from './BBCARUploadStep'

// Services
import { businessService } from '../_services'

// Constants
import ROUTES from '../_constants/routes'
import { tenantColors } from '../_constants/colors'

// Helpers
import { history } from '../_helpers'
import { getInvoicesTotal } from '../_helpers/invoices'

// Actions
import { borrowingBaseCertificateActions } from '../_actions/borrowingBaseCertificate.actions'

// Hardcoded values
// Only for White Oak Demo
import { hardcodedValues } from './constants'
import { COMPANY_NAME } from 'xen/constants'

// Styled Componenets
const BorrowingBaseCertificate = styled.div`
  .main-content {
    position: relative;
    width: 80%;
    max-width: 800px;
    margin: 0 auto;
    padding: 50px 0px;
  }

  ${S_FormTracker} {
    margin: 0 0 30px;
  }

  .title {
    margin-bottom: 0;
    position: relative;
    text-align: center;
    font-size: 30px;
    margin-bottom: 30px;
    .back-link {
      position: absolute;
      height: 100%;
      width: 16px;
      left: -25px;
      opacity: 0.25;
      transition: opacity 0.2s ease-in-out;
      cursor: pointer;

      &:hover {
        opacity: 0.75;
      }
    }
    span {
      display: block;
    }
  }

  .loading-spinner {
    margin: 0 auto;
    width: 100px;
  }

  .sections {
    width: 80vw;
    max-width: 800px;
    &.hidden {
      display: none;
    }
  }

  .slick-arrow {
    display: none !important;
  }

  .slick-list,
  .slick-track {
    height: auto !important;
    overflow: hidden;
  }
`

class BorrowingBaseCertificatePage extends React.Component {
  constructor(props) {
    super(props)

    this.slider = React.createRef()
    this.currentSection = 0

    this.state = {
      submitted: false,
      inReview: false,
      config: null,
      activeSection: 1,
      completed: [],
      isInUploadStep: true,
      invoices: [],
      invoiceAssetId: null,
      documents: [],
    }
  }

  componentDidMount() {
    const { business } = this.props
    const businessId = business && business.id ? business.id : this.props.businessId
    const { activeSection } = this.state
    if (this.slider.current) {
      this.slider.current.slickGoTo(activeSection - 1)
    }

    // Get Config
    businessService.getClientBbcConfig(businessId).then(
      (resp) => {
        // resp.forEach(section => {
        //     console.log(section.ineligibles)
        //     section.ineligibles = JSON.parse(section.ineligibles)
        // })
        this.setState({
          config: resp,
          hasLoadedConfig: true,
        })
        this.createSections()
      },
      (_) => this.setState({ config: [] })
    )

    // Get Last submitted BBC, then check if unsubmitted one exists, else
    businessService
      .getLastSubmittedBbc(businessId)
      .then(
        (resp) => {
          const bbc = resp[0] && resp[0].asset_bbc_form_template_sections
          this.setState({
            lastBbc: [], // WB DEMO: removed check  bbc || [],
            hasLoadedLastBbc: true,
          })
          this.createSections()
        },
        (_) => this.setState({ lastBbc: [] })
      )
      .then((resp) => {
        businessService.getUnsubmittedBbc(businessId).then((resp) => {
          if (resp && resp.length) {
            // Using existing unsubmitted Bbc if one is found
            this.setState({
              currentBbc: resp[0].asset_bbc_form_template_sections,
              documents: resp[0].documents,
              hasLoadedCurrentBbc: true,
              bbcId: resp[0].id,
            })
            this.createSections()
          } else {
            businessService.createNewBbc().then((resp) => {
              // Else, start new Bbc
              this.setState({
                currentBbc: resp.asset_bbc_form_template_sections,
                documents: resp.documents,
                hasLoadedCurrentBbc: true,
                bbcId: resp.id,
              })
              this.createSections()
            })
          }
        })
      })
  }

  formatInitialCurrencyValue = (amount) => {
    return amount && amount !== '0.0' ? amount : ''
  }

  /**
   * Uses the 'config', 'lastBbc', and 'sectionConfig' to construct a flat state
   * for the BBC submission form.
   */
  createSections = () => {
    const { config, hasLoadedConfig, lastBbc, hasLoadedLastBbc, currentBbc, hasLoadedCurrentBbc } = this.state

    // Skip if any response hasn't loaded
    if (!hasLoadedConfig || !hasLoadedLastBbc || !hasLoadedCurrentBbc) {
      return
    }

    // Tenant Config Data
    const tenantId = this.props.tenant
    const generalConfigData = borrowingBaseCertificateConfig
    const tenantConfigData = generalConfigData[tenantId]
    const uiSections = tenantConfigData.sections
    const { activeSection } = this.state

    const assets = {}
    const ineligibles = {}
    const sectionTypeVals = {
      'Accounts Receivable': 3,
      Inventory: 2,
      Equipment: 1,
      'Term Loan': 4,
    }

    // Sort so that in order of array above
    const sortedConfig = [...config].sort((a, b) => {
      return sectionTypeVals[b.section_type] - sectionTypeVals[a.section_type]
    })

    // Create a flatted state for the Borrowing Base form
    const formSections = sortedConfig.map((configSection, i) => {
      // Add front-end config data
      const correctSectionData = uiSections.filter((uiSection) => {
        return uiSection.section_type === configSection.section_type
      })[0]

      // console.log('correct section data')
      // console.log(correctSectionData)
      const completeSection = {
        ...correctSectionData,
        rateTitle: generalConfigData.rateTitle,
        marginedAmount: generalConfigData.marginedAmount,
      }

      // Add section assets (current and last)
      // Note: since there's only one asset per section, we can use the labels in the section config data

      let correctCurrentSection = currentBbc[i]

      // Monkey patch for demo. Not sure why inventory and equipment seem reversed
      // but the labels on their ineligibles aren't.
      // TODO: fix so this is not necessary.
      if (i == 1) {
        correctCurrentSection = currentBbc[2]
      } else if (i == 2) {
        correctCurrentSection = currentBbc[1]
      }

      const currentAssetRef = correctCurrentSection.asset
      const currentAssetVal = currentAssetRef.value
      const asset = {
        id: currentAssetRef.id,
        advance_rate: currentAssetRef.advance_rate,
        ineligibles: [],
        // value: this.formatInitialCurrencyValue(currentAssetVal),
        value: hardcodedValues[configSection.section_type],
        date: currentAssetRef.date,
      }
      completeSection['assetId'] = asset.id
      assets[asset.id] = asset

      // If we have invoices, save the accounts receivable id to set value of later
      if (configSection.section_type === 'Accounts Receivable') {
        this.setState({
          invoiceAssetId: asset.id,
        })
      }

      let lastAssetRef
      if (lastBbc && lastBbc.length) {
        const correctLastSection = lastBbc[i]
        // .filter(lastBbcSection => {
        //     return lastBbcSection.bbc_form_template_section_id === configSection.id
        // })[0]
        lastAssetRef = correctLastSection.asset
        const lastAssetVal = lastAssetRef.value
        asset['last_advance_rate'] = lastAssetRef.advance_rate
        asset['last_date'] = lastAssetRef.date
        asset['last_value'] = this.formatInitialCurrencyValue(lastAssetVal)
      }

      // Add ineligibles (current and last)

      const currentAssetIneligibles = currentAssetRef.asset_ineligibles
      const ineligibleLabels = configSection.ineligibles.labels
      currentAssetIneligibles.forEach((assetIneligible, i) => {
        const ineligibleId = assetIneligible.id
        const ineligable = {
          id: ineligibleId,
          label: ineligibleLabels[i],
          // value: this.formatInitialCurrencyValue(assetIneligible.value),
          value: this.formatInitialCurrencyValue(
            hardcodedValues[`${configSection.section_type}-${ineligibleLabels[i]}`]
          ),
        }
        if (lastBbc && lastBbc.length) {
          const lastAssetIneligibles = lastAssetRef.asset_ineligibles
          ineligable.last_value = lastAssetIneligibles[i].value
        }
        asset['ineligibles'].push(ineligibleId)
        ineligibles[ineligibleId] = ineligable
      })

      return completeSection
    })

    // Set State
    this.setState({
      formSections: formSections,
      assets: assets,
      ineligibles: ineligibles,
      showLastBbc: !!lastBbc && !!lastBbc.length,
      currentSection: activeSection - 1,
    })
  }

  /**
   * Updates the component state for a specific item (asset, ineligible)
   */
  updateItem(itemId, itemType, field, newValue) {
    this.setState((prevState) => {
      const prevItems = prevState[itemType]
      const newItems = {}
      Object.values(prevItems).forEach((item) => {
        const newItem = { ...item }
        if (item.id === itemId) {
          newItem[field] = newValue
        }
        newItems[item.id] = newItem
      })
      return {
        [itemType]: newItems,
      }
    })
  }

  /**
   * Saves (service call) the item data for an asset or ineligible
   */
  handleSaveItemData = (itemType, itemId, itemField, itemValue) => {
    if (itemType === 'asset') {
      businessService.saveAssetData(itemId, { [itemField]: itemValue })
    } else {
      businessService.saveIneligibleData(itemId, { [itemField]: itemValue })
    }
  }

  handleUpdateAssetValue = (assetId, newValue) => {
    this.updateItem(assetId, 'assets', 'value', newValue)
  }

  handleUpdateAssetDate = (assetId, newDate) => {
    this.updateItem(assetId, 'assets', 'date', newDate)
  }

  handleUpdateIneligibleValue = (assetId, newValue) => {
    this.updateItem(assetId, 'ineligibles', 'value', newValue)
  }

  handleChange = (setFieldValue, attr, val) => {
    setFieldValue(attr, val)
  }

  handleGoToReview = () => {
    this.setState({
      inReview: true,
    })
  }

  /**
   * Move the slider to a specific section.
   * Note: Validation is incomplete / inactive.
   */
  handleSectionChange = (sectionIndex, validateForm, sections) => {
    const { activeSection } = this.state

    const section = sections[activeSection - 1]

    this.setState({
      inReview: false,
    })

    validateForm().then((errors) => {
      if (Object.keys(errors).length === 0) {
        let newCompleted = this.state.completed
        newCompleted[activeSection - 1] = true
        this.setState({
          activeSection: sectionIndex,
          completed: newCompleted,
        })
      } else {
        let newCompleted = this.state.completed
        newCompleted[activeSection - 1] = this.isSectionComplete(errors, section)
        this.setState({
          activeSection: sectionIndex,
          completed: newCompleted,
        })
      }
      this.slider.current.slickGoTo(sectionIndex - 1)
    })
  }

  /**
   * Method stub for future validation
   */
  isSectionComplete = (errors, section) => {
    return true
  }

  /**
   * Go back from the review page to edit a specific section
   */
  handleEditForm = () => {
    const { activeSection } = this.state
    this.setState({
      inReview: false,
    })
    this.slider.current.slickGoTo(activeSection - 1)
  }

  /**
   * Call to complete the BBC submission and go to the document upload page
   */
  handleCompleteForm = (validateForm, values) => {
    const { setBbcId } = this.props
    this.setState({ submitted: true })
    businessService.submitBbc(this.state.bbcId).then(
      (resp) => {
        setBbcId(resp.id)
        history.push(ROUTES.abl_client_home)
      },
      (error) => {
        this.setState({
          submitted: false,
          inReview: false,
        })
      }
    )
  }

  handleSetInvoices = (newInvoices, shouldUpdateAsset = false) => {
    const invoiceAssetId = this.state.invoiceAssetId
    if (invoiceAssetId !== null && shouldUpdateAsset) {
      const invoicesTotalValue = getInvoicesTotal(newInvoices)
      this.handleUpdateAssetValue(this.state.invoiceAssetId, invoicesTotalValue)
      this.handleSaveItemData('asset', invoiceAssetId, 'value', invoicesTotalValue)
    }

    this.setState({
      invoices: newInvoices,
    })
  }

  handleInvoicesComplete = () => {
    this.setState({
      isInUploadStep: false,
    })
  }

  handleAddDocument = (newDocument) => {
    this.setState((state, props) => {
      const currentDocuments = state.documents
      const newDocuments = currentDocuments.concat(newDocument)
      return {
        documents: newDocuments,
      }
    })
  }

  render() {
    const { tenant } = this.props

    // TODO: remove unused state fields from declaration
    const {
      activeSection,
      completed,
      submitted,
      formSections,
      assets,
      ineligibles,
      inReview,
      showLastBbc,
      bbcId,
      isInUploadStep,
      invoices,
      documents,
    } = this.state

    // Show Loader if form data isn't set
    if (!formSections || !assets || !ineligibles) {
      return (
        <BorrowingBaseCertificate>
          <div className={`main-content`}>
            <div className={`loading-spinner`}>
              <ClipLoader color={tenantColors[tenant].secondary} loading={true} size={100} />
            </div>
          </div>
        </BorrowingBaseCertificate>
      )
    }

    return (
      <BorrowingBaseCertificate>
        <Helmet>
          <title>
            {borrowingBaseCertificateConfig.title} • {COMPANY_NAME}
          </title>
        </Helmet>
        <div className={`main-content`}>
          <h1 className={`title`}>
            <FaArrowLeft
              className={`back-link`}
              onClick={() => {
                if (isInUploadStep) {
                  history.push(ROUTES.abl_client_home)
                } else {
                  this.setState({
                    isInUploadStep: true,
                  })
                }
              }}
            />
            <span>Borrowing Base Submission</span>
          </h1>

          {/* Upload A/R Invoices Step */}
          {isInUploadStep ? (
            <BBCARUploadStep
              bbcId={bbcId}
              invoices={invoices}
              onComplete={this.handleInvoicesComplete}
              setInvoices={this.handleSetInvoices}
            />
          ) : (
            <Formik>
              {({ setFieldTouched, setFieldValue, values, errors, touched, isValid, validateForm }) => (
                <Form className={`borrowing-base-certificate`}>
                  {!this.state.inReview && (
                    <FormTracker
                      currentSection={this.currentSection}
                      onSectionChange={(newSection) => {
                        this.currentSection = newSection
                        this.handleSectionChange(newSection + 1, validateForm, formSections)
                      }}
                      sections={formSections}
                    />
                  )}
                  <Slider className={`sections ${inReview ? 'hidden' : ''}`} ref={this.slider}>
                    {formSections.map((formSection, i) => {
                      const sectionAsset = assets[formSection.assetId]
                      return (
                        <BBCFormSection
                          activeSection={activeSection}
                          bbcId={bbcId}
                          currentSection={this.currentSection}
                          documents={documents}
                          formSection={formSection}
                          handleAddDocument={this.handleAddDocument}
                          handleGoToReview={this.handleGoToReview}
                          handleSaveItemData={this.handleSaveItemData}
                          handleSectionChange={(sectionId) => {
                            this.handleSectionChange(sectionId, validateForm, formSections)
                          }}
                          handleUpdateAssetDate={this.handleUpdateAssetDate}
                          handleUpdateAssetValue={this.handleUpdateAssetValue}
                          handleUpdateIneligibleValue={this.handleUpdateIneligibleValue}
                          ineligibles={ineligibles}
                          invoices={invoices}
                          key={i}
                          numSections={formSections.length}
                          onSectionChange={(newSection) => {
                            this.currentSection = newSection
                          }}
                          sectionAsset={sectionAsset}
                          showLast={showLastBbc}
                        />
                      )
                    })}
                  </Slider>
                  {inReview && (
                    <BBCSummary
                      assets={assets}
                      formSections={formSections}
                      handleCompleteForm={() => {
                        this.handleCompleteForm(validateForm, null)
                      }}
                      handleSectionChange={(sectionId) => {
                        this.handleSectionChange(sectionId, validateForm, formSections)
                      }}
                      ineligibles={ineligibles}
                      showLast={showLastBbc}
                    />
                  )}
                </Form>
              )}
            </Formik>
          )}
        </div>
      </BorrowingBaseCertificate>
    )
  }
}

function mapStateToProps(state) {
  const { env, user, business } = state
  return {
    tenant: env.tenant,
    user,
    business,
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    setBbcId: (id) => {
      dispatch(borrowingBaseCertificateActions.setId(id))
    },
  }
}

const connectedBorrowingBaseCertificatePage = connect(mapStateToProps, mapDispatchToProps)(BorrowingBaseCertificatePage)
export { connectedBorrowingBaseCertificatePage as BorrowingBaseCertificatePage }
